<template>
	<b-modal ref="modal" :busy="isBusy" :hide-footer="pane === 0">
		<template #modal-header="{ close }">
			<div class="d-flex align-items-center w-100">
				<b-button v-if="canGoBack" variant="link" size="sm" class="p-0 mr-2 text-body" @click="goBack">
					<b-icon icon="arrow-left"></b-icon>
				</b-button>
				<h5 class="modal-title mb-0 flex-fill">{{modalTitle}}</h5>
				<button type="button" class="close ml-2" @click="close()"><span>&times;</span></button>
			</div>
		</template>

		<div class="pane-outer">
			<div class="pane-inner" :style="paneInnerStyle">

				<!-- Pane 0: Service select -->
				<div class="pane">
					<div class="text-center py-3" v-if="servicesLoading > 0">
						<b-icon icon="circle-fill" animation="throb" font-scale="3"></b-icon>
					</div>
					<b-alert variant="danger" :show="!!servicesError">{{servicesError}}</b-alert>
					<template v-if="servicesLoading === 0 && !servicesError">
						<b-input v-model="serviceFilter" placeholder="Search..." class="mb-2"/>
						<b-form-row cols-lg="4" align-v="stretch">
							<b-col v-for="(service, idx) in filteredServices" :key="idx" align-self="stretch" class="mb-2">
								<b-button block class="h-100" @click="openService(service, $event)">
									<img v-if="service.icon_url" class="rounded-circle d-block mx-auto mb-1" :src="service.icon_url" style="max-width: 40px" />
									{{service.name}}
								</b-button>
							</b-col>
						</b-form-row>
					</template>
				</div>

				<!-- Pane 1: Search -->
				<div class="pane">
					<b-form-group description="Enter the name or the URL of your channel">
						<b-input-group>
							<b-form-input @input="channelSearch" v-model="channelSearchString" placeholder="Search..."></b-form-input>
							<b-input-group-append is-text v-if="searching > 0">
								<b-spinner small></b-spinner>
							</b-input-group-append>
						</b-input-group>
					</b-form-group>
					<div style="max-height: 260px; overflow-y: auto;" v-if="channelSuggestions.length">
						<b-list-group flush>
							<b-list-group-item v-for="(c, idx) in channelSuggestions" :key="idx" class="d-flex align-items-center px-2 py-2 channel-item">
								<b-avatar :src="c.thumbnail" size="2.5em" class="mr-3 flex-shrink-0"></b-avatar>
								<div class="flex-fill overflow-hidden mr-3">
									<div class="font-weight-bold text-truncate">{{c.name || c.token}}</div>
									<div class="text-muted small text-truncate" v-if="c.name">{{c.token}}</div>
								</div>
								<b-button variant="primary" size="sm" :disabled="saving > 0" @click="addChannel(c)">
									Add <b-icon icon="arrow-right"></b-icon>
								</b-button>
							</b-list-group-item>
						</b-list-group>
					</div>
					<b-alert variant="danger" :show="!!searchError" class="mt-2 mb-2">{{searchError}}</b-alert>
				</div>

				<!-- Pane 2: Edit -->
				<div class="pane">
					<div class="text-center py-3" v-if="editLoading > 0">
						<b-icon icon="circle-fill" animation="throb" font-scale="3"></b-icon>
					</div>
					<b-alert variant="danger" show v-else-if="!!editError">{{editError}}</b-alert>
					<b-form @submit.stop.prevent="save" id="channelEditForm" v-else>
						<b-avatar size="2em" :src="curChannel?.avatar_url||curChannel?.service_icon_url" badge-variant="" badge-offset="-0.4em">
							<template #badge><b-avatar v-if="curChannel?.avatar_url" :src="curChannel?.service_icon_url||null" size="1.3em" class="service_avatar" square :title="curChannel.service_name"></b-avatar></template>
						</b-avatar>
						{{curChannel.title}}
						<hr>
						<fieldset :disabled="saving > 0">
							<b-form-group label="Hide in">
								<b-form-checkbox v-model="curChannel.flags" value="no-viewer-count" switch>Viewer count widget</b-form-checkbox>
								<b-form-checkbox v-model="curChannel.flags" value="no-chat" switch>Chat widget</b-form-checkbox>
								<b-form-checkbox v-model="curChannel.flags" value="no-gift" switch>Alert widget</b-form-checkbox>
							</b-form-group>
							<b-form-group v-for="(field, i) in curChannel.protocolData?.channelSettingFields" :key="i" :label="field.type != 'bool' ? `${field.label||i}${field.required?' (required)':''}` : null">
								<b-form-input v-if="field.type == 'int' || field.type == 'float'" v-model.number="curChannel.data[i]" type="number"></b-form-input>
								<b-form-input v-else-if="field.type == 'color' || field.type == 'password' || field.type == 'email'" v-model="curChannel.data[i]" :type="field.type" :required="field.required"></b-form-input>
								<b-form-checkbox v-else-if="field.type == 'bool'" v-model="curChannel.data[i]" :value="true" :unchecked-value="false">{{field.label||i}}</b-form-checkbox>
								<b-form-checkbox-group v-else-if="field.type == 'checkList'" v-model="curChannel.data[i]" :options="field.options" stacked :state="curChannel.data[i]?.length > 0 ? null : false" invalid-feedback="Please select at least one option." :required="field.required && !curChannel.data[i]?.length"/>
								<b-form-input v-else v-model="curChannel.data[i]"></b-form-input>
							</b-form-group>
							<b-form-group label="Stream URL (RTMP)" v-if="user?.is_pro && !curChannel.protocolData?.noStreaming">
								<b-form-input v-model="curChannel.stream_url"></b-form-input>
							</b-form-group>
							<b-form-group label="Stream Key" v-if="user?.is_pro && !curChannel.protocolData?.noStreaming">
								<b-form-input type="password" v-model="curChannel.stream_key"></b-form-input>
							</b-form-group>
							<b-form-group label="Verify channel as yours" v-if="!curChannel.verified">
								<b-input-group>
									<b-input ref="verificationCommand" v-model="curChannel.verificationCommand" @focus="$event.target.select()"></b-input>
									<b-input-group-append>
										<b-button v-if="curChannel.verificationCommand" variant="outline-secondary" title="Copy to clipboard" v-b-tooltip.hover @click="copyVerificationCommandToClipboard"><b-icon icon="clipboard"></b-icon></b-button>
										<b-button variant="primary" title="Generate" v-b-tooltip.hover @click="generateVerificationCommand">Generate</b-button>
									</b-input-group-append>
									<p class="text-muted">Without verification, you cannot use commands, timers and bridging in this channel. The bot will not send any messages to it.</p>
								</b-input-group>
								<b-form-text>{{curChannel.verificationCommandMessage}}</b-form-text>
							</b-form-group>
							<b-form-group label="Bridging" v-if="curChannel.verified && user?.bridging_status == 'enabled'">
								<b-form-checkbox v-if="!curChannel.protocolData?.readOnly" v-model="curChannel.bridging_no_receive">Don't receive messages from other platforms</b-form-checkbox>
								<b-form-checkbox v-model="curChannel.bridging_no_send">Don't send messages to other platforms</b-form-checkbox>
							</b-form-group>
						</fieldset>
					</b-form>
				</div>
			</div>
		</div>

		<template #modal-footer="{ cancel }">
			<template v-if="pane === 1">
				<b-button size="sm" @click="cancel()">Cancel</b-button>
			</template>
			<div class="w-100" v-else-if="pane === 2">
				<b-alert variant="danger" :show="!!savingError">{{savingError}}</b-alert>
				<div class="float-left">
					<a href="#" @click.prevent.stop="showDeleteModal()" class="text-danger"><b-icon icon="trash"></b-icon> Delete</a>
				</div>
				<div class="float-right">
					<b-spinner v-show="saving > 0" small label="Spinning" class="mr-3"></b-spinner>
					<b-button size="sm" class="mr-2" v-if="curChannel.auth" @click.prevent="openAuthPopup(curChannel.auth.url)" :href="curChannel.auth.url" target="_blank">Reauthenticate <b-icon icon="arrow-up-right-square-fill"></b-icon></b-button>
					<b-button size="sm" variant="success" type="submit" form="channelEditForm" class="mr-2">Save</b-button>
					<b-button size="sm" @click="cancel()">Cancel</b-button>
				</div>
			</div>
		</template>

		<channel-delete-modal ref="channelDeleteModal" @deleted="$refs.modal.hide(); $emit('saved')"></channel-delete-modal>
	</b-modal>
</template>

<script>
import debounce from 'debounce';
import ChannelDeleteModal from './Delete';

export default {
	name: 'ChannelModal',
	components: { ChannelDeleteModal },
	data() { return {
		pane: 0,
		isAddFlow: false,

		// Pane 0 (service select)
		servicesLoading: 0,
		servicesError: null,
		services: [],
		serviceFilter: '',

		// Pane 0→1
		serviceId: null,
		serviceName: null,

		// Pane 1 (search)
		channelSearchString: '',
		channelSuggestions: [],
		searching: 0,
		searchError: null,
		saving: 0,

		// Pane 2 (edit)
		editLoading: 0,
		editError: null,
		savingError: null,
		user: null,
		channelToken: null,
		curChannel: { title: '', token: '', data: {}, flags: [] },
	}},
	computed: {
		modalTitle() {
			if(this.pane === 0) return 'Add a channel';
			if(this.pane === 1) return this.serviceName ? `Add ${this.serviceName} channel` : 'Add a channel';
			return this.isAddFlow ? 'Configure channel' : 'Edit channel';
		},
		canGoBack() {
			return this.pane === 1 || (this.pane === 2 && this.isAddFlow);
		},
		isBusy() {
			if(this.pane === 0) return this.servicesLoading > 0;
			if(this.pane === 1) return this.saving > 0;
			return this.editLoading > 0;
		},
		filteredServices() {
			const f = this.serviceFilter.toLowerCase();
			return f ? this.services.filter(s => s.name.toLowerCase().startsWith(f)) : this.services;
		},
		paneInnerStyle() {
			return { transform: `translateX(calc(-100% * ${this.pane} / 3))` };
		},
	},
	methods: {
		// Public API
		show() {
			this.pane = 0;
			this.isAddFlow = false;
			this.serviceFilter = '';
			this.channelSuggestions = [];
			this.searchError = null;
			this.$refs.modal.show();
			this.loadServices();
		},
		showEdit(serviceId, channelToken) {
			this.pane = 2;
			this.isAddFlow = false;
			this.serviceId = serviceId;
			this.channelToken = channelToken;
			this.curChannel = {
				title: '',
				token: '',
				data: {},
				flags: [],
				verificationCommand: '',
				verificationCommandMessage: '',
			};
			this.$refs.modal.show();
			this.loadEditData();
		},

		goBack() {
			this.pane--;
		},

		// Pane 0
		async loadServices() {
			try {
				this.servicesLoading++;
				this.servicesError = null;
				const all = await this.$api.get('services');
				this.services = all.filter(s => s.capabilities.includes('chat'));
			} catch(e) {
				this.servicesError = e?.error || e;
			} finally {
				this.servicesLoading--;
			}
		},
		openService(service, event) {
			if(service.auth && service.auth.url && !event?.shiftKey) {
				window.open(service.auth.url, 'Authenticate', `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=${service.auth.width||700},height=${service.auth.height||600}`);
				this.$refs.modal.hide();
				return;
			}
			this.serviceId = service.id;
			this.serviceName = service.name;
			this.channelSearchString = '';
			this.channelSuggestions = [];
			this.searchError = null;
			this.pane = 1;
		},

		// Pane 1
		channelSearch: debounce(function() {
			if(!this.serviceId) return;
			this.searching++;
			this.channelSuggestions = [];
			this.searchError = null;
			this.$api
				.get(`channels/${encodeURIComponent(this.serviceId)}/search`, { q: this.channelSearchString })
				.then(results => {
					this.channelSuggestions = results.map(r => ({ ...r, title: r.name||r.token }));
				}).catch(err => {
					console.error('Channel search', err);
					this.channelSuggestions = [];
					this.searchError = err?.error||err;
				}).finally(() => {
					this.searching--;
				});
		}, 300),
		async addChannel(item) {
			try {
				this.saving++;
				this.searchError = null;
				await this.$api.post('channel', { data: {
					serviceId: this.serviceId,
					token: item.token,
					title: item.title,
					avatar_url: item.thumbnail,
					data: item.data||{},
				}});
				this.$emit('saved');
				this.channelToken = item.token;
				this.isAddFlow = true;
				this.curChannel = {
					title: '',
					token: '',
					data: {},
					flags: [],
					verificationCommand: '',
					verificationCommandMessage: '',
				};
				this.pane = 2;
				this.loadEditData();
			} catch(e) {
				this.searchError = e?.error||e;
			} finally {
				this.saving--;
			}
		},

		// Pane 2
		async loadEditData() {
			try {
				this.editLoading++;
				this.editError = null;
				this.savingError = null;
				[this.user, this.curChannel] = await Promise.all([
					this.$api.get('userData'),
					this.$api.get(`channel/${encodeURIComponent(this.serviceId)}/${encodeURIComponent(this.channelToken)}`).then(c => ({ verificationCommand: '', verificationCommandMessage: '', ...c, data: c.data || {}, flags: c.flags || [] })),
				]);
			} catch(e) {
				this.editError = e;
			} finally {
				this.editLoading--;
			}
		},
		async save() {
			try {
				this.saving++;
				this.savingError = null;
				await this.$api.patch(`channel/${encodeURIComponent(this.serviceId)}/${encodeURIComponent(this.channelToken)}`, {
					data: this.curChannel.data,
					stream_url: this.curChannel.stream_url,
					stream_key: this.curChannel.stream_key,
					flags: this.curChannel.flags || [],
				});
				this.$refs.modal.hide();
				this.$bvToast.toast('Successfully updated channel', {
					title: 'Success',
					variant: 'success',
					solid: true
				});
				this.$emit('saved');
			} catch(e) {
				this.savingError = e;
			} finally {
				this.saving--;
			}
		},
		openAuthPopup(authUrl) {
			window.open(authUrl, 'Authenticate', 'scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=600,height=500');
		},
		async generateVerificationCommand() {
			try {
				this.loading++;
				const result = await this.$api.get(`channel/${encodeURIComponent(this.curChannel.service_id)}/${encodeURIComponent(this.curChannel.token)}/verificationCode`);
				this.curChannel.verificationCommand = `!verify ${result.code}`;
				this.curChannel.verificationCommandMessage = `Post this command, as the channel owner, in your chat. Expires in ${result.expiryMinutes} minutes.`;
			} catch(e) {
				console.error(e);
				this.error = e;
			} finally {
				this.loading--;
			}
		},
		copyVerificationCommandToClipboard() {
			this.$refs.verificationCommand.focus();
			document.execCommand('copy');
		},
		showDeleteModal() {
			this.$refs.channelDeleteModal.show(this.curChannel.service_id, this.curChannel.token, this.curChannel.service_name, this.curChannel.protocol_name);
		},
	},
};
</script>

<style scoped lang="scss">
@import '@/plugins/variable.scss';

.pane-outer {
	overflow: hidden;
}

.pane-inner {
	display: flex;
	width: 300%;
	transition: transform 0.3s ease;

	.pane {
		flex: 0 0 calc(100% / 3);
		min-width: 0;
		min-height: 200px;
	}
}

.channel-item {
	background-color: $gray-200;

	&:nth-child(odd) {
		background-color: $gray-400;
	}
}
</style>
